// Drawing App - Main Entry Point

import { BrushSettings, ToolType, ShapeType, SymmetryMode, BrushPresetType, TextureType, Point, ViewTransform, CustomBrushData } from './types';
import { TransformManager } from './transform';
import { LayerManager } from './layer_manager';
import { CanvasEngine } from './canvas_engine';
import { BrushEngine } from './brush_engine';
import { Stabilizer, SmoothingLevel } from './stabilizer';
import { HistoryManager } from './history_manager';
import { SymmetryEngine } from './symmetry';
import { ShapeDetector } from './shape_detector';
import { SelectionManager } from './selection';
import { ExportManager } from './export_manager';
import { ColorPicker } from './color_picker';
import { ToolbarUI } from './toolbar_ui';
import { createTool, Tool, ToolContext } from './tools';
import { DrawingStorage, DrawingSaveData, LayerSaveData } from './persistence';
import { BrushCursor } from './brush_cursor';
import { getPreset, applyPreset } from './brush_presets';
import { RealtimeSync } from './realtime_sync';
import { RelayManager } from './relay_manager';
import { ChatPanel } from './chat_panel';
import { CatchMindManager } from './catch_mind_manager';
import { TextureManager } from './texture_manager';
import { CustomBrushEditor } from './custom_brush';
import { PresetStorage } from './preset_storage';

export class DrawingApp {
  private displayCanvas!: HTMLCanvasElement;
  private transformManager!: TransformManager;
  private layerManager!: LayerManager;
  private canvasEngine!: CanvasEngine;
  private brushEngine!: BrushEngine;
  private stabilizer!: Stabilizer;
  private historyManager!: HistoryManager;
  private symmetryEngine!: SymmetryEngine;
  private shapeDetector!: ShapeDetector;
  private selectionManager!: SelectionManager;
  private exportManager!: ExportManager;
  private colorPicker!: ColorPicker;
  private toolbarUI!: ToolbarUI;
  private currentTool!: Tool;
  private brushCursor!: BrushCursor;
  private realtimeSync: RealtimeSync | null = null;
  private relayManager: RelayManager | null = null;
  private chatPanel: ChatPanel | null = null;
  private catchMindManager: CatchMindManager | null = null;
  private drawingEnabled = true;
  private currentPreset: BrushPresetType = 'default';
  private textureManager!: TextureManager;
  private customBrushEditor!: CustomBrushEditor;
  private presetStorage!: PresetStorage;
  private currentCustomBrushData: CustomBrushData | null = null;

  private brushSettings: BrushSettings = {
    size: 12,
    opacity: 0.8,
    flow: 1.0,
    spacing: 0.15,
    hardness: 0.8,
    jitter: 0,
    smoothing: 2,
    symmetry: 'none',
    texture: null,
    textureType: 'none',
    textureStrength: 0.5,
    customBrushImage: null
  };

  private primaryColor = '#000000';
  private shapeType: ShapeType = 'rectangle';
  private spacePressed = false;
  private canvasWidth = 2048;
  private canvasHeight = 2048;
  private roomCode: string = '';
  private storage: DrawingStorage = new DrawingStorage();
  private visibilityHandler: (() => void) | null = null;
  private beforeUnloadHandler: ((e: BeforeUnloadEvent) => void) | null = null;

  init(container: HTMLElement): void {
    const displayCanvas = container.querySelector('#drawing-canvas') as HTMLCanvasElement;
    const toolbarEl = container.querySelector('#drawing-toolbar') as HTMLElement;
    const layerPanelEl = container.querySelector('#drawing-layers') as HTMLElement;
    const propertiesBarEl = container.querySelector('#drawing-properties') as HTMLElement;
    const colorPickerEl = container.querySelector('#drawing-color-picker') as HTMLElement;

    if (!displayCanvas) return;
    this.displayCanvas = displayCanvas;

    // Initialize managers
    this.transformManager = new TransformManager();
    this.layerManager = new LayerManager(this.canvasWidth, this.canvasHeight);
    this.canvasEngine = new CanvasEngine(displayCanvas, this.layerManager, this.transformManager, this.canvasWidth, this.canvasHeight);
    this.brushEngine = new BrushEngine();
    this.textureManager = new TextureManager();
    this.brushEngine.setTextureManager(this.textureManager);
    this.customBrushEditor = new CustomBrushEditor();
    this.presetStorage = new PresetStorage();
    this.stabilizer = new Stabilizer();
    this.historyManager = new HistoryManager();
    this.symmetryEngine = new SymmetryEngine();
    this.shapeDetector = new ShapeDetector();
    this.selectionManager = new SelectionManager();
    this.exportManager = new ExportManager(this.canvasEngine, this.layerManager);

    // Color picker
    if (colorPickerEl) {
      this.colorPicker = new ColorPicker(colorPickerEl);
      this.colorPicker.setColor(this.primaryColor);
      this.colorPicker.onChange((color) => {
        this.primaryColor = color;
      });
    }

    // Default tool
    this.currentTool = createTool('brush');

    // Toolbar UI
    if (toolbarEl && layerPanelEl && propertiesBarEl) {
      this.toolbarUI = new ToolbarUI(toolbarEl, layerPanelEl, propertiesBarEl, {
        onToolChange: (tool) => this.setTool(tool),
        onBrushSettingsChange: (settings) => {
          Object.assign(this.brushSettings, settings);
          if (this.brushCursor) this.brushCursor.updateBrushSettings(this.brushSettings);
        },
        onPresetChange: (preset) => this.applyBrushPreset(preset),
        onShapeTypeChange: (shape) => { this.shapeType = shape; },
        onSymmetryChange: (mode) => this.symmetryEngine.setMode(mode),
        onSmoothingChange: (level) => this.stabilizer.setLevel(level),
        onUndo: () => { this.historyManager.undo(); this.canvasEngine.requestRender(); this.updateUI(); },
        onRedo: () => { this.historyManager.redo(); this.canvasEngine.requestRender(); this.updateUI(); },
        onAddLayer: () => { this.layerManager.addLayer(); this.updateUI(); },
        onRemoveLayer: () => { this.layerManager.removeLayer(this.layerManager.getActiveLayerId()); this.updateUI(); },
        onDuplicateLayer: () => { this.layerManager.duplicateLayer(this.layerManager.getActiveLayerId()); this.updateUI(); },
        onMoveLayerUp: () => { this.layerManager.moveLayer(this.layerManager.getActiveLayerId(), 'up'); this.updateUI(); },
        onMoveLayerDown: () => { this.layerManager.moveLayer(this.layerManager.getActiveLayerId(), 'down'); this.updateUI(); },
        onMergeDown: () => { this.layerManager.mergeDown(this.layerManager.getActiveLayerId()); this.updateUI(); },
        onClearLayer: () => { this.layerManager.clearLayer(this.layerManager.getActiveLayerId()); this.canvasEngine.requestRender(); },
        onSelectLayer: (id) => { this.layerManager.setActiveLayer(id); this.updateUI(); },
        onLayerVisibility: (id, visible) => { this.layerManager.setLayerVisibility(id, visible); this.canvasEngine.requestRender(); this.updateUI(); },
        onLayerOpacity: (id, opacity) => { this.layerManager.setLayerOpacity(id, opacity); this.canvasEngine.requestRender(); },
        onExportPNG: () => this.exportManager.exportPNG(),
        onExportSVG: () => this.exportManager.downloadSVG(),
        onResetView: () => { this.transformManager.resetView(this.canvasWidth, this.canvasHeight); },
        onZoomIn: () => { const cw = displayCanvas.width / 2; const ch = displayCanvas.height / 2; this.transformManager.zoom(-1, cw, ch); },
        onZoomOut: () => { const cw = displayCanvas.width / 2; const ch = displayCanvas.height / 2; this.transformManager.zoom(1, cw, ch); },
        onTextureChange: (type) => {
          this.brushSettings.textureType = type;
          if (type === 'none') {
            this.brushSettings.textureStrength = 0;
          } else if (this.brushSettings.textureStrength === 0) {
            this.brushSettings.textureStrength = 0.5;
          }
        },
        onTextureStrengthChange: (strength) => {
          this.brushSettings.textureStrength = strength;
        },
        onCustomBrushEdit: () => {
          this.customBrushEditor.open(
            (data) => this.applyCustomBrush(data),
            () => this.clearCustomBrush(),
            this.currentCustomBrushData?.dataURL
          );
        },
        onSavePreset: () => this.saveCurrentAsPreset(),
        onLoadPreset: (id) => this.loadSavedPreset(id),
        onDeletePreset: (id) => this.deleteSavedPreset(id),
      }, this.brushSettings);

      // Load saved presets into toolbar
      this.toolbarUI.updateSavedPresets(this.presetStorage.getAll());
    }

    // Layer/history change listeners
    this.layerManager.onChange(() => { this.canvasEngine.requestRender(); this.updateUI(); });
    this.historyManager.onChange(() => {
      this.updateUI();
      if (this.roomCode) {
        this.storage.scheduleSave(this.roomCode, () => this.getState());
      }
    });
    this.transformManager.onChange(() => this.updateUI());

    // Setup canvas events
    this.setupCanvasEvents();
    this.setupKeyboardShortcuts();
    this.setupMobileGestures();
    this.resizeCanvas();
    window.addEventListener('resize', () => this.resizeCanvas());

    // Initialize brush cursor
    this.brushCursor = new BrushCursor(this.canvasEngine, this.transformManager, displayCanvas);
    this.brushCursor.updateBrushSettings(this.brushSettings);

    // Initial UI update
    this.updateUI();

    // Center view
    this.transformManager.fitToScreen(this.canvasWidth, this.canvasHeight, displayCanvas.width, displayCanvas.height);

    // Extract room code from URL or container data attribute
    const pathMatch = window.location.pathname.match(/\/drawing\/room\/([^/]+)/);
    this.roomCode = pathMatch ? pathMatch[1] : '';

    // Initialize persistence
    if (this.roomCode) {
      this.initPersistence();

      // Initialize realtime sync
      const userId = container.getAttribute('data-user-id') || '';
      const userName = container.getAttribute('data-user-name') || '';
      const hostId = container.getAttribute('data-host-id') || '';
      if (userId) {
        this.realtimeSync = new RealtimeSync(
          this.roomCode,
          this.canvasEngine,
          this.canvasWidth,
          this.canvasHeight,
          userId,
          userName
        );

        // Initialize relay manager
        const isHost = userId === hostId;
        const userIdNum = parseInt(userId, 10);
        this.relayManager = new RelayManager(
          this.roomCode,
          userIdNum,
          userName,
          isHost,
          (enabled) => { this.drawingEnabled = enabled; }
        );
        const channel = this.realtimeSync.getChannel();
        if (channel) {
          this.relayManager.bindToChannel(channel);
        }

        // Initialize chat panel
        this.chatPanel = new ChatPanel(this.roomCode, userIdNum, userName);
        if (channel) {
          this.chatPanel.bindToChannel(channel);
        }

        // Initialize catch mind manager
        this.catchMindManager = new CatchMindManager(
          this.roomCode,
          userIdNum,
          userName,
          isHost,
          (enabled) => { this.drawingEnabled = enabled; }
        );
        if (channel) {
          this.catchMindManager.bindToChannel(channel);
        }
        this.catchMindManager.setChatPanel(this.chatPanel);

        // Setup game mode selection dropdown (replaces old relay-only dropdown)
        if (isHost) {
          this.setupGameModeDropdown();
        }
      }
    }
  }

  private setTool(type: ToolType): void {
    this.currentTool = createTool(type);
    if (this.toolbarUI) this.toolbarUI.setActiveTool(type);

    // Enable brush cursor only for brush/eraser tools
    if (this.brushCursor) {
      this.brushCursor.setEnabled(type === 'brush' || type === 'eraser');
    }
  }

  private applyBrushPreset(presetType: BrushPresetType): void {
    this.currentPreset = presetType;
    const preset = getPreset(presetType);
    this.brushSettings = applyPreset(this.brushSettings, preset);
    this.brushEngine.setPreset(presetType);
    if (this.brushCursor) this.brushCursor.updateBrushSettings(this.brushSettings);
    if (this.toolbarUI) this.toolbarUI.setActivePreset(presetType);
  }

  private getToolContext(): ToolContext {
    return {
      layerManager: this.layerManager,
      canvasEngine: this.canvasEngine,
      historyManager: this.historyManager,
      brushEngine: this.brushEngine,
      stabilizer: this.stabilizer,
      symmetryEngine: this.symmetryEngine,
      shapeDetector: this.shapeDetector,
      transformManager: this.transformManager,
      brushSettings: this.brushSettings,
      primaryColor: this.primaryColor,
      shapeType: this.shapeType,
      onColorPicked: (color) => {
        this.primaryColor = color;
        if (this.colorPicker) this.colorPicker.setColor(color);
      }
    };
  }

  private setupCanvasEvents(): void {
    const canvas = this.displayCanvas;
    let isPointerDown = false;

    canvas.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      canvas.setPointerCapture(e.pointerId);
      isPointerDown = true;

      // Pan with space or middle mouse button
      if (this.spacePressed || e.button === 1) {
        this.transformManager.startPan(e.clientX, e.clientY);
        return;
      }

      // Block drawing when relay is active and it's not my turn
      if (!this.drawingEnabled) return;

      const rect = canvas.getBoundingClientRect();
      const screenX = e.clientX - rect.left;
      const screenY = e.clientY - rect.top;
      const canvasPos = this.transformManager.screenToCanvas(screenX, screenY);

      const point: Point = {
        x: canvasPos.x,
        y: canvasPos.y,
        pressure: e.pressure || 0.5,
        tiltX: e.tiltX || 0,
        tiltY: e.tiltY || 0,
        timestamp: e.timeStamp
      };

      if (this.brushCursor) this.brushCursor.setDrawing(true);
      this.currentTool.onPointerDown(point, this.getToolContext());
    });

    canvas.addEventListener('pointermove', (e) => {
      // Update realtime cursor position
      const rect = canvas.getBoundingClientRect();
      const screenX = e.clientX - rect.left;
      const screenY = e.clientY - rect.top;
      const canvasPos = this.transformManager.screenToCanvas(screenX, screenY);

      if (this.realtimeSync) {
        this.realtimeSync.updateLocalCursor(canvasPos.x, canvasPos.y);
      }

      if (!isPointerDown) return;

      if (this.spacePressed || this.transformManager.getIsPanning()) {
        this.transformManager.movePan(e.clientX, e.clientY);
        return;
      }

      const point: Point = {
        x: canvasPos.x,
        y: canvasPos.y,
        pressure: e.pressure || 0.5,
        tiltX: e.tiltX || 0,
        tiltY: e.tiltY || 0,
        timestamp: e.timeStamp
      };

      this.currentTool.onPointerMove(point, this.getToolContext());
    });

    canvas.addEventListener('pointerup', (e) => {
      if (!isPointerDown) return;
      isPointerDown = false;

      if (this.transformManager.getIsPanning()) {
        this.transformManager.endPan();
        if (this.brushCursor) this.brushCursor.setDrawing(false);
        return;
      }

      const rect = canvas.getBoundingClientRect();
      const screenX = e.clientX - rect.left;
      const screenY = e.clientY - rect.top;
      const canvasPos = this.transformManager.screenToCanvas(screenX, screenY);

      const point: Point = {
        x: canvasPos.x,
        y: canvasPos.y,
        pressure: e.pressure || 0.5,
        tiltX: e.tiltX || 0,
        tiltY: e.tiltY || 0,
        timestamp: e.timeStamp
      };

      // Broadcast stroke to remote peers before ending
      if (this.realtimeSync && this.currentTool.type === 'brush') {
        const strokePoints = this.brushEngine.getStrokePoints();
        if (strokePoints.length > 0) {
          this.realtimeSync.broadcastStroke(strokePoints, this.brushSettings, this.primaryColor, this.currentPreset);
        }
      }

      this.currentTool.onPointerUp(point, this.getToolContext());
      if (this.brushCursor) this.brushCursor.setDrawing(false);
    });

    // Zoom with wheel
    canvas.addEventListener('wheel', (e) => {
      e.preventDefault();
      const rect = canvas.getBoundingClientRect();
      const screenX = e.clientX - rect.left;
      const screenY = e.clientY - rect.top;
      this.transformManager.zoom(e.deltaY, screenX, screenY);
    }, { passive: false });

    // Touch events for pinch zoom
    let lastTouches: Touch[] = [];
    canvas.addEventListener('touchstart', (e) => {
      if (e.touches.length === 2) {
        e.preventDefault();
        lastTouches = Array.from(e.touches);
        this.transformManager.startPinch(
          { x: e.touches[0].clientX, y: e.touches[0].clientY },
          { x: e.touches[1].clientX, y: e.touches[1].clientY }
        );
      }
    }, { passive: false });

    canvas.addEventListener('touchmove', (e) => {
      if (e.touches.length === 2) {
        e.preventDefault();
        this.transformManager.movePinch(
          { x: e.touches[0].clientX, y: e.touches[0].clientY },
          { x: e.touches[1].clientX, y: e.touches[1].clientY }
        );
      }
    }, { passive: false });

    // Prevent context menu
    canvas.addEventListener('contextmenu', (e) => e.preventDefault());
  }

  private setupKeyboardShortcuts(): void {
    document.addEventListener('keydown', (e) => {
      // Don't capture if typing in input
      if ((e.target as HTMLElement).tagName === 'INPUT' || (e.target as HTMLElement).tagName === 'TEXTAREA') return;

      if (e.code === 'Space') {
        e.preventDefault();
        this.spacePressed = true;
        return;
      }

      if (e.ctrlKey || e.metaKey) {
        if (e.code === 'KeyZ' && !e.shiftKey) {
          e.preventDefault();
          this.historyManager.undo();
          this.canvasEngine.requestRender();
        } else if ((e.code === 'KeyZ' && e.shiftKey) || e.code === 'KeyY') {
          e.preventDefault();
          this.historyManager.redo();
          this.canvasEngine.requestRender();
        } else if (e.code === 'Equal') {
          e.preventDefault();
          const cw = this.displayCanvas.width / 2;
          const ch = this.displayCanvas.height / 2;
          this.transformManager.zoom(-1, cw, ch);
        } else if (e.code === 'Minus') {
          e.preventDefault();
          const cw = this.displayCanvas.width / 2;
          const ch = this.displayCanvas.height / 2;
          this.transformManager.zoom(1, cw, ch);
        } else if (e.code === 'Digit0') {
          e.preventDefault();
          this.transformManager.fitToScreen(this.canvasWidth, this.canvasHeight, this.displayCanvas.width, this.displayCanvas.height);
        }
        return;
      }

      // Tool shortcuts
      switch (e.code) {
        case 'KeyB': this.setTool('brush'); break;
        case 'KeyE': this.setTool('eraser'); break;
        case 'KeyS': this.setTool('shape'); break;
        case 'KeyG': this.setTool('fill'); break;
        case 'KeyI': this.setTool('eyedropper'); break;
        case 'KeyM': this.setTool('select'); break;
        case 'BracketLeft':
          this.brushSettings.size = Math.max(1, this.brushSettings.size - 5);
          this.updateUI();
          break;
        case 'BracketRight':
          this.brushSettings.size = Math.min(200, this.brushSettings.size + 5);
          this.updateUI();
          break;
      }
    });

    document.addEventListener('keyup', (e) => {
      if (e.code === 'Space') {
        this.spacePressed = false;
      }
    });
  }

  private setupMobileGestures(): void {
    const canvas = this.displayCanvas;

    // 3-finger tap = undo
    let touchStartCount = 0;
    let touchStartTime = 0;

    canvas.addEventListener('touchstart', (e) => {
      if (e.touches.length === 3) {
        e.preventDefault();
        touchStartCount = 3;
        touchStartTime = Date.now();
      }
    }, { passive: false });

    canvas.addEventListener('touchend', (e) => {
      if (touchStartCount === 3 && Date.now() - touchStartTime < 400) {
        // 3-finger tap detected - undo
        this.historyManager.undo();
        this.canvasEngine.requestRender();
        this.updateUI();
        touchStartCount = 0;
      }
    });
  }

  private resizeCanvas(): void {
    const container = this.displayCanvas.parentElement!;
    const rect = container.getBoundingClientRect();
    this.canvasEngine.resizeDisplay(rect.width, rect.height);
  }

  private updateUI(): void {
    if (!this.toolbarUI) return;

    // Update layer panel
    const layers = this.layerManager.getLayers().map(l => ({
      id: l.id,
      name: l.name,
      visible: l.visible,
      opacity: l.opacity,
      locked: l.locked
    }));
    this.toolbarUI.updateLayerPanel(layers, this.layerManager.getActiveLayerId());

    // Update history buttons
    this.toolbarUI.updateHistoryButtons(this.historyManager.canUndo(), this.historyManager.canRedo());

    // Update zoom
    this.toolbarUI.updateZoomLevel(this.transformManager.getScalePercent());
  }

  private async initPersistence(): Promise<void> {
    try {
      await this.storage.open();

      // Try to restore from IndexedDB
      const saved = await this.storage.loadState(this.roomCode);
      if (saved) {
        await this.restoreState(saved);
      }

      // Save on visibility change (tab switch / app background)
      this.visibilityHandler = () => {
        if (document.hidden && this.storage.isDirty()) {
          this.storage.saveNow(this.roomCode, () => this.getState());
        }
      };
      document.addEventListener('visibilitychange', this.visibilityHandler);

      // Save on page unload
      this.beforeUnloadHandler = (e: BeforeUnloadEvent) => {
        if (this.storage.isDirty()) {
          this.storage.saveNow(this.roomCode, () => this.getState());
          e.preventDefault();
          e.returnValue = '';
        }
      };
      window.addEventListener('beforeunload', this.beforeUnloadHandler);

      // Update save status indicator
      this.storage.onStatusChange((status) => {
        const el = document.getElementById('drawing-save-status');
        if (!el) return;
        switch (status) {
          case 'saved':
            el.textContent = '✓ 저장됨';
            el.className = 'text-xs text-green-400 font-medium';
            break;
          case 'saving':
            el.textContent = '↻ 저장 중...';
            el.className = 'text-xs text-yellow-400 font-medium animate-pulse';
            break;
          case 'dirty':
            el.textContent = '● 변경됨';
            el.className = 'text-xs text-orange-400 font-medium';
            break;
        }
      });
    } catch (e) {
      console.error('[DrawingApp] Persistence init failed:', e);
    }
  }

  private getState(): DrawingSaveData {
    const layers: LayerSaveData[] = this.layerManager.getLayers().map(layer => {
      // Convert OffscreenCanvas to data URL via a regular canvas
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = this.canvasWidth;
      tempCanvas.height = this.canvasHeight;
      const ctx = tempCanvas.getContext('2d')!;
      ctx.drawImage(layer.canvas as any, 0, 0);
      const imageDataURL = tempCanvas.toDataURL('image/png');

      return {
        id: layer.id,
        name: layer.name,
        opacity: layer.opacity,
        blendMode: layer.blendMode,
        visible: layer.visible,
        imageDataURL
      };
    });

    return {
      layers,
      activeLayerId: this.layerManager.getActiveLayerId(),
      viewTransform: this.transformManager.getTransform(),
      brushSettings: { ...this.brushSettings },
      primaryColor: this.primaryColor,
      savedAt: Date.now()
    };
  }

  private async restoreState(data: DrawingSaveData): Promise<void> {
    if (!data.layers || data.layers.length === 0) return;

    // Clear existing layers
    const existingLayers = this.layerManager.getLayers();
    existingLayers.forEach(l => {
      if (existingLayers.length > 1) {
        this.layerManager.removeLayer(l.id);
      }
    });

    // Restore layers
    for (let i = 0; i < data.layers.length; i++) {
      const layerData = data.layers[i];
      const layer = i === 0 ? this.layerManager.getLayers()[0] : this.layerManager.addLayer(layerData.name);
      if (!layer) continue;

      // Set layer properties
      this.layerManager.setLayerOpacity(layer.id, layerData.opacity);
      this.layerManager.setLayerVisibility(layer.id, layerData.visible);

      // Restore image data
      if (layerData.imageDataURL) {
        await new Promise<void>((resolve) => {
          const img = new Image();
          img.onload = () => {
            const ctx = (layer.canvas as OffscreenCanvas).getContext('2d')!;
            ctx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);
            ctx.drawImage(img, 0, 0);
            resolve();
          };
          img.onerror = () => resolve();
          img.src = layerData.imageDataURL;
        });
      }
    }

    // Restore active layer
    if (data.activeLayerId) {
      this.layerManager.setActiveLayer(data.activeLayerId);
    }

    // Restore brush settings
    if (data.brushSettings) {
      Object.assign(this.brushSettings, data.brushSettings);
    }

    // Restore color
    if (data.primaryColor) {
      this.primaryColor = data.primaryColor;
      if (this.colorPicker) this.colorPicker.setColor(data.primaryColor);
    }

    // Re-render
    this.canvasEngine.requestRender();
    this.updateUI();
  }

  private async applyCustomBrush(data: CustomBrushData): Promise<void> {
    this.currentCustomBrushData = data;
    this.brushSettings.customBrushImage = data.dataURL;
    await this.brushEngine.setCustomBrush(data.dataURL);
    if (this.toolbarUI) this.toolbarUI.setCustomBrushActive(true);
  }

  private async clearCustomBrush(): Promise<void> {
    this.currentCustomBrushData = null;
    this.brushSettings.customBrushImage = null;
    await this.brushEngine.setCustomBrush(null);
    if (this.toolbarUI) this.toolbarUI.setCustomBrushActive(false);
  }

  private saveCurrentAsPreset(): void {
    const name = prompt('프리셋 이름:');
    if (!name || !name.trim()) return;

    this.presetStorage.save(
      name.trim(),
      this.brushSettings,
      this.primaryColor,
      this.currentPreset,
      this.currentCustomBrushData
    );

    if (this.toolbarUI) {
      this.toolbarUI.updateSavedPresets(this.presetStorage.getAll());
    }
  }

  private async loadSavedPreset(id: string): Promise<void> {
    const preset = this.presetStorage.getById(id);
    if (!preset) return;

    // Apply brush preset type first
    this.currentPreset = preset.presetType;
    this.brushEngine.setPreset(preset.presetType);

    // Apply settings
    this.brushSettings = { ...preset.settings };
    this.primaryColor = preset.color;
    if (this.colorPicker) this.colorPicker.setColor(preset.color);

    // Apply custom brush
    if (preset.customBrushData) {
      await this.applyCustomBrush(preset.customBrushData);
    } else {
      await this.clearCustomBrush();
    }

    if (this.brushCursor) this.brushCursor.updateBrushSettings(this.brushSettings);
    if (this.toolbarUI) {
      this.toolbarUI.setActivePreset(preset.presetType);
      this.toolbarUI.updateBrushSettings(this.brushSettings);
    }
  }

  private deleteSavedPreset(id: string): void {
    this.presetStorage.delete(id);
    if (this.toolbarUI) {
      this.toolbarUI.updateSavedPresets(this.presetStorage.getAll());
    }
  }

  private setupGameModeDropdown(): void {
    const startBtn = document.getElementById('relay-start-btn');
    const stopBtn = document.getElementById('relay-stop-btn');
    if (!startBtn) return;

    // Override the relay manager's click handler with game mode selector
    const newBtn = startBtn.cloneNode(true) as HTMLElement;
    startBtn.parentNode?.replaceChild(newBtn, startBtn);
    newBtn.id = 'relay-start-btn';
    newBtn.classList.remove('hidden');
    newBtn.classList.add('flex');

    newBtn.addEventListener('click', () => {
      this.showGameModeDropdown(newBtn);
    });

    // Stop button handles both relay and catch mind
    if (stopBtn) {
      const newStopBtn = stopBtn.cloneNode(true) as HTMLElement;
      stopBtn.parentNode?.replaceChild(newStopBtn, stopBtn);
      newStopBtn.id = 'relay-stop-btn';

      newStopBtn.addEventListener('click', () => {
        if (this.catchMindManager?.isActive()) {
          this.catchMindManager.stopGame();
        } else {
          this.relayManager?.stopRelay();
        }
      });
    }
  }

  private showGameModeDropdown(anchorEl: HTMLElement): void {
    const existingDropdown = document.getElementById('game-mode-dropdown');
    if (existingDropdown) { existingDropdown.remove(); return; }

    const dropdown = document.createElement('div');
    dropdown.id = 'game-mode-dropdown';
    dropdown.className = 'absolute top-12 right-0 bg-gray-800 border border-gray-700 rounded-lg p-4 shadow-xl z-50';
    dropdown.style.minWidth = '240px';
    dropdown.innerHTML = `
      <div class="text-white text-sm font-bold mb-3">게임 모드 선택</div>
      <div class="mb-3">
        <select id="game-mode-select" class="w-full bg-gray-700 text-white text-sm rounded px-2 py-1.5 border border-gray-600">
          <option value="relay">🎨 릴레이 그리기</option>
          <option value="catchmind">🧠 캐치마인드</option>
        </select>
      </div>

      <!-- Relay settings -->
      <div id="relay-settings" class="">
        <div class="mb-3">
          <label class="text-gray-400 text-xs block mb-1">턴 시간</label>
          <select id="game-turn-duration" class="w-full bg-gray-700 text-white text-sm rounded px-2 py-1.5 border border-gray-600">
            <option value="15">15초</option>
            <option value="30" selected>30초</option>
            <option value="60">60초</option>
          </select>
        </div>
        <div class="mb-3">
          <label class="text-gray-400 text-xs block mb-1">라운드 수</label>
          <select id="game-max-rounds" class="w-full bg-gray-700 text-white text-sm rounded px-2 py-1.5 border border-gray-600">
            <option value="1">1 라운드</option>
            <option value="2">2 라운드</option>
            <option value="3" selected>3 라운드</option>
            <option value="4">4 라운드</option>
            <option value="5">5 라운드</option>
          </select>
        </div>
      </div>

      <!-- Catch Mind settings -->
      <div id="catchmind-settings" class="hidden">
        <div class="mb-3">
          <label class="text-gray-400 text-xs block mb-1">제한 시간</label>
          <select id="catchmind-duration" class="w-full bg-gray-700 text-white text-sm rounded px-2 py-1.5 border border-gray-600">
            <option value="60">60초</option>
            <option value="90" selected>90초</option>
            <option value="120">120초</option>
          </select>
        </div>
        <div class="mb-3">
          <label class="text-gray-400 text-xs block mb-1">라운드 수</label>
          <select id="catchmind-rounds" class="w-full bg-gray-700 text-white text-sm rounded px-2 py-1.5 border border-gray-600">
            ${Array.from({length: 10}, (_, i) => `<option value="${i+1}" ${i+1 === 3 ? 'selected' : ''}>${i+1} 라운드</option>`).join('')}
          </select>
        </div>
      </div>

      <button id="game-start-btn" class="w-full bg-purple-600 hover:bg-purple-700 text-white text-sm font-bold rounded-lg py-2 transition-colors">
        시작하기
      </button>
    `;

    anchorEl.parentElement!.style.position = 'relative';
    anchorEl.parentElement!.appendChild(dropdown);

    // Toggle settings visibility based on mode
    const modeSelect = document.getElementById('game-mode-select') as HTMLSelectElement;
    const relaySettings = document.getElementById('relay-settings')!;
    const catchmindSettings = document.getElementById('catchmind-settings')!;

    modeSelect.addEventListener('change', () => {
      const mode = modeSelect.value;
      relaySettings.classList.toggle('hidden', mode !== 'relay');
      catchmindSettings.classList.toggle('hidden', mode !== 'catchmind');
    });

    document.getElementById('game-start-btn')?.addEventListener('click', () => {
      const mode = modeSelect.value;
      dropdown.remove();

      if (mode === 'relay') {
        const duration = parseInt((document.getElementById('game-turn-duration') as HTMLSelectElement).value, 10);
        const rounds = parseInt((document.getElementById('game-max-rounds') as HTMLSelectElement).value, 10);
        this.relayManager?.startRelay({ turnDuration: duration, maxRounds: rounds });
      } else {
        const duration = parseInt((document.getElementById('catchmind-duration') as HTMLSelectElement).value, 10);
        const rounds = parseInt((document.getElementById('catchmind-rounds') as HTMLSelectElement).value, 10);
        this.catchMindManager?.startGame(duration, rounds);
      }
    });

    // Close on outside click
    const closeHandler = (e: MouseEvent) => {
      if (!dropdown.contains(e.target as Node) && e.target !== anchorEl) {
        dropdown.remove();
        document.removeEventListener('click', closeHandler);
      }
    };
    setTimeout(() => document.addEventListener('click', closeHandler), 0);
  }

  async saveToCloud(): Promise<boolean> {
    if (!this.roomCode) return false;

    const statusEl = document.getElementById('drawing-save-status');
    if (statusEl) {
      statusEl.textContent = '☁ 업로드 중...';
      statusEl.className = 'text-xs text-blue-400 font-medium animate-pulse';
    }

    try {
      // Get composite image as blob
      const compositeCanvas = document.createElement('canvas');
      compositeCanvas.width = this.canvasWidth;
      compositeCanvas.height = this.canvasHeight;
      const ctx = compositeCanvas.getContext('2d')!;

      const layers = this.layerManager.getLayers();
      for (const layer of layers) {
        if (!layer.visible) continue;
        ctx.globalAlpha = layer.opacity;
        ctx.globalCompositeOperation = layer.blendMode;
        ctx.drawImage(layer.canvas as any, 0, 0);
      }
      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = 'source-over';

      const blob = await new Promise<Blob>((resolve) => {
        compositeCanvas.toBlob((b) => resolve(b!), 'image/png');
      });

      // Prepare layer metadata
      const layersMeta = layers.map(l => ({
        id: l.id,
        name: l.name,
        opacity: l.opacity,
        blendMode: l.blendMode,
        visible: l.visible
      }));

      const formData = new FormData();
      formData.append('canvas_image', blob, 'canvas.png');
      formData.append('layers_json', JSON.stringify(layersMeta));

      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
      const response = await fetch(`/drawing/room/${this.roomCode}/save`, {
        method: 'POST',
        headers: { 'X-CSRF-Token': csrfToken },
        body: formData
      });

      const result = await response.json();
      if (result.success) {
        if (statusEl) {
          statusEl.textContent = '☁ 클라우드 저장됨';
          statusEl.className = 'text-xs text-green-400 font-medium';
        }
        return true;
      } else {
        throw new Error(result.error || 'Save failed');
      }
    } catch (e) {
      console.error('[DrawingApp] Cloud save failed:', e);
      if (statusEl) {
        statusEl.textContent = '☁ 저장 실패';
        statusEl.className = 'text-xs text-red-400 font-medium';
      }
      return false;
    }
  }

  async loadFromCloud(): Promise<boolean> {
    if (!this.roomCode) return false;

    const statusEl = document.getElementById('drawing-save-status');
    if (statusEl) {
      statusEl.textContent = '☁ 불러오는 중...';
      statusEl.className = 'text-xs text-blue-400 font-medium animate-pulse';
    }

    try {
      const response = await fetch(`/drawing/room/${this.roomCode}/load`);
      if (!response.ok) {
        if (response.status === 404) {
          if (statusEl) {
            statusEl.textContent = '저장된 데이터 없음';
            statusEl.className = 'text-xs text-gray-400 font-medium';
          }
          return false;
        }
        throw new Error('Load failed');
      }

      const data = await response.json();
      if (!data.canvas_url) return false;

      // Load the composite image onto the first layer
      await new Promise<void>((resolve) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          const activeLayer = this.layerManager.getActiveLayer();
          if (activeLayer) {
            const ctx = (activeLayer.canvas as OffscreenCanvas).getContext('2d')!;
            ctx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);
            ctx.drawImage(img, 0, 0);
          }
          this.canvasEngine.requestRender();
          resolve();
        };
        img.onerror = () => resolve();
        img.src = data.canvas_url;
      });

      if (statusEl) {
        statusEl.textContent = '☁ 불러옴';
        statusEl.className = 'text-xs text-green-400 font-medium';
      }
      return true;
    } catch (e) {
      console.error('[DrawingApp] Cloud load failed:', e);
      if (statusEl) {
        statusEl.textContent = '불러오기 실패';
        statusEl.className = 'text-xs text-red-400 font-medium';
      }
      return false;
    }
  }

  destroy(): void {
    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
    }
    if (this.beforeUnloadHandler) {
      window.removeEventListener('beforeunload', this.beforeUnloadHandler);
    }
    // Save before destroy
    if (this.roomCode && this.storage.isDirty()) {
      this.storage.saveNow(this.roomCode, () => this.getState());
    }
    if (this.catchMindManager) {
      this.catchMindManager.destroy();
    }
    if (this.chatPanel) {
      this.chatPanel.destroy();
    }
    if (this.relayManager) {
      this.relayManager.destroy();
    }
    if (this.realtimeSync) {
      this.realtimeSync.destroy();
    }
    if (this.brushCursor) {
      this.brushCursor.destroy();
    }
    this.storage.destroy();
    this.canvasEngine.destroy();
  }
}

// Auto-initialize when DOM is ready
function initDrawingApp() {
  const container = document.getElementById('drawing-app-container');
  if (!container) return;

  // Prevent double initialization
  if ((window as any).__drawingApp) {
    (window as any).__drawingApp.destroy();
    (window as any).__drawingApp = null;
  }

  const app = new DrawingApp();
  app.init(container);
  (window as any).__drawingApp = app;
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initDrawingApp);
} else {
  initDrawingApp();
}

// Also support Turbo navigation
document.addEventListener('turbo:load', initDrawingApp);
